// B-139 — the team's presentation: Reportes' "Equipo" tab as a PowerPoint, for Brinks' own follow-up
// (internal — it says who did what). Laid out like the client's committee deck (`cierreComitePptx.js`):
// the same cover photograph and Brinks logo, the blue band, the blue title, the yellow rule and the
// confidentiality footer, and the same thanks. Every chart is native and every figure is written on
// it, so each one can be read and edited. It follows the screen's period and its person filter.
//
// `presentacionDelEquipo(PptxGenJS, datos, opciones)` is pure but for pptxgenjs, so a test can hand
// it a recording stand-in and check every slide without writing a file.

import { IMAGENES, comoDatos } from "./cierreComitePptx";
import {
  TIPOS_DE_TRABAJO, TIPO_DE_ALERTA, colaPorRegion, horaBogota, horasLegibles, nombreDePersona, resumenDelEquipo, serieDeFlujo, serieDeRespuesta, trabajoPorTipo,
} from "./cierreEquipo";

// What this deck uses of the committee's pictures.
const USADAS = ["portada", "bandaSuperior", "logoBrinksAzul", "logoBrinksBlanco", "degradado", "gracias"];

const AZUL = "1A55B5";
const AZUL_OSCURO = "1F3864";
const AMARILLO = "F5B800";
const ROJO = "D64545";
const NARANJA = "F2B33D";
const VERDE = "3F9A64";
const TINTA = "1F2937";
const GRIS = "6B7280";
const LINEA = "D9D9D9";
const FONDO_FILA = "F2F2F2";
const FUENTE = "Arial";
const TRABAJO = ["0F172A", "3D5A99", "6F86C2", "3F9A64", "E07D20", "C3CCDF"];
const FILAS_POR_TABLA = 18;

const p = (puntos) => puntos / 72;

/**
 * The deck.
 *
 * @param {Function} PptxGenJS  the pptxgenjs constructor
 * @param {object} datos        `getCierreTeamActivity`'s data
 * @param {{ cliente: string, desde: string, hasta: string, persona?: string|null,
 *           nombreDeAlerta?: Function, imagenes?: object }} opciones  dates as dd-mm-aaaa
 */
export function presentacionDelEquipo(PptxGenJS, datos, {
  cliente, desde, hasta, persona = null, nombreDeAlerta = (k) => k, imagenes = {},
}) {
  const I = { ...IMAGENES, ...imagenes };
  const pptx = new PptxGenJS();
  pptx.layout = "LAYOUT_WIDE";
  pptx.title = `Informe del equipo ${cliente} ${desde} a ${hasta}`;
  pptx.company = "Brinks";
  const anio = new Date().getFullYear();
  const periodo = `Del ${desde} al ${hasta}`;
  const quien = persona ? nombreDePersona(persona) : null;
  const resumen = resumenDelEquipo(datos, persona);
  const gente = datos.people.filter((x) => !persona || x.persona === persona);
  const respuestas = datos.responses.personas.filter((x) => !persona || x.persona === persona);

  const pie = (slide, color = "595959") => {
    slide.addShape("rect", { x: p(27), y: p(498), w: p(28), h: p(28), fill: { color: "FFFFFF" }, line: { color: "7F7F7F", width: 0.75 } });
    slide.addText(`©${anio} BRINK'S. CONFIDENTIAL. FOR INTERNAL USE ONLY – DO NOT DUPLICATE OR DISTRIBUTE.`, {
      x: p(62), y: p(505), w: p(520), h: p(16), fontFace: FUENTE, fontSize: 7, color,
    });
  };
  const regla = (slide) => slide.addShape("rect", { x: 0, y: p(535), w: p(960), h: p(5), fill: { color: AMARILLO }, line: { color: AMARILLO, width: 0 } });

  // A content slide: the blue band, the title, the logo, the yellow rule, the footer.
  const contenido = (titulo, subtitulo) => {
    const slide = pptx.addSlide();
    slide.background = { color: "FFFFFF" };
    slide.addImage({ path: I.bandaSuperior, x: 0, y: 0, w: p(960), h: p(11) });
    slide.addText(titulo, { x: p(34), y: p(18), w: p(720), h: p(50), fontFace: FUENTE, fontSize: 28, color: AZUL });
    slide.addText(subtitulo || periodo, { x: p(36), y: p(64), w: p(720), h: p(22), fontFace: FUENTE, fontSize: 12, color: GRIS });
    slide.addImage({ path: I.logoBrinksAzul, x: p(792), y: p(38), w: p(113), h: p(19.5) });
    regla(slide);
    pie(slide);
    return slide;
  };
  const vacio = (slide, texto) => slide.addText(texto, {
    x: p(40), y: p(240), w: p(880), h: p(30), fontFace: FUENTE, fontSize: 13, color: GRIS, align: "center",
  });

  // Tight cell margins (points): PowerPoint's own would make every row twice the height asked.
  const celda = (text, extra = {}) => ({
    text: String(text ?? ""), options: { fontFace: FUENTE, fontSize: 9, color: TINTA, valign: "middle", margin: [2, 5, 2, 5], ...extra },
  });
  const encabezado = (text, align = "center") => celda(text, { bold: true, color: "FFFFFF", fill: { color: AZUL_OSCURO }, align });
  const tabla = (slide, columnas, filas, { x = 34, y = 100, w = 892 } = {}) => {
    slide.addTable([
      columnas.map((c) => encabezado(c.titulo, c.align || "center")),
      ...filas.map((f, i) => f.map((v, j) => celda(v, { align: columnas[j].align || "center", ...(i % 2 ? { fill: { color: FONDO_FILA } } : {}) }))),
    ], {
      x: p(x), y: p(y), w: p(w), colW: columnas.map((c) => p(c.ancho)), rowH: p(19), border: { type: "solid", color: LINEA, pt: 0.5 },
    });
  };
  // A long table over as many slides as it needs.
  const tablaPaginada = (titulo, subtitulo, columnas, filas, vacia) => {
    if (!filas.length) {
      vacio(contenido(titulo, subtitulo), vacia);
      return;
    }
    for (let i = 0; i < filas.length; i += FILAS_POR_TABLA) {
      const total = Math.ceil(filas.length / FILAS_POR_TABLA);
      const parte = total > 1 ? ` (${i / FILAS_POR_TABLA + 1} de ${total})` : "";
      tabla(contenido(`${titulo}${parte}`, subtitulo), columnas, filas.slice(i, i + FILAS_POR_TABLA));
    }
  };
  const ejes = {
    catAxisLabelFontSize: 9, valAxisLabelFontSize: 8, valGridLine: { color: "E5E7EB", size: 0.5 }, legendFontSize: 9, showLegend: true, legendPos: "b",
  };

  // 1. The cover.
  const tapa = pptx.addSlide();
  tapa.addImage({ path: I.portada, x: 0, y: 0, w: p(960), h: p(540), sizing: { type: "cover", w: p(960), h: p(540) } });
  tapa.addImage({ path: I.logoBrinksBlanco, x: p(28), y: p(34), w: p(112), h: p(19.5) });
  tapa.addText("INFORME DEL EQUIPO", { x: p(370), y: p(30), w: p(570), h: p(52), fontFace: FUENTE, fontSize: 32, color: "FFFFFF" });
  tapa.addShape("line", { x: p(372), y: p(80), w: p(560), h: 0, line: { color: AMARILLO, width: 2 } });
  tapa.addText(`${cliente} · ${periodo}${quien ? ` · ${quien}` : ""}`, { x: p(372), y: p(88), w: p(560), h: p(28), fontFace: FUENTE, fontSize: 16, color: "FFFFFF" });
  pie(tapa, "E5E7EB");

  // 2. The headline figures, as the screen's cards.
  const res = contenido("Resumen del periodo", quien ? `${periodo} · ${quien}` : periodo);
  [
    { etiqueta: quien ? "Alertas que atendió" : "Alertas atendidas", valor: String(resumen.atendidas), detalle: resumen.atendidas ? `${resumen.pctEnUnDia} % en menos de un día` : "en el periodo", hero: true },
    { etiqueta: "Cola abierta hoy", valor: String(resumen.descuadres + resumen.revisiones), detalle: `${resumen.descuadres} descuadres · ${resumen.revisiones} por revisar` },
    { etiqueta: "Tiempo promedio de atención", valor: horasLegibles(resumen.promedioHoras), detalle: "Desde que se levanta hasta que se atiende" },
    { etiqueta: "Días procesados", valor: String(resumen.diasProcesados), detalle: quien ? `${resumen.acciones} acciones` : `${resumen.porCorreo} por correo · ${resumen.personas} personas` },
  ].forEach((t, i) => {
    const x = p(34 + i * 226);
    res.addShape("rect", { x, y: p(110), w: p(214), h: p(130), fill: { color: t.hero ? AZUL_OSCURO : "FFFFFF" }, line: { color: t.hero ? AZUL_OSCURO : LINEA, width: 0.75 } });
    res.addText(t.etiqueta, { x: x + p(12), y: p(120), w: p(190), h: p(20), fontFace: FUENTE, fontSize: 11, color: t.hero ? "D1D5DB" : GRIS });
    res.addText(t.valor, { x: x + p(12), y: p(146), w: p(190), h: p(44), fontFace: FUENTE, fontSize: 30, bold: true, color: t.hero ? "FFFFFF" : TINTA });
    res.addText(t.detalle, { x: x + p(12), y: p(196), w: p(190), h: p(34), fontFace: FUENTE, fontSize: 9, color: t.hero ? "D1D5DB" : GRIS, valign: "top" });
  });
  const cierres = datos.queue.cierres;
  res.addText([
    { text: `Cierres del periodo: ${cierres.total} · cuadrados ${cierres.cuadrados} · por atender ${cierres.pendientes}.`, options: { breakLine: true } },
    { text: `Descuadres abiertos con más de 7 días: ${resumen.descuadresViejos}.`, options: { breakLine: true } },
    { text: `Oficinas con trabajo abierto hoy: ${(datos.backlog || []).length}.` },
  ], { x: p(34), y: p(268), w: p(892), h: p(90), fontFace: FUENTE, fontSize: 13, color: TINTA, valign: "top", paraSpaceAfter: 6 });

  // 3. Alerts in and out per day.
  const flujo = serieDeFlujo(datos.flow, { persona });
  const entrada = contenido("Entrada y salida de alertas", `${periodo} · descuadres y revisiones levantadas cada día frente a las atendidas`);
  if (flujo.length) {
    entrada.addChart(pptx.ChartType.bar, [
      { name: "Levantadas", labels: flujo.map((d) => d.etiqueta), values: flujo.map((d) => d.descuadres + d.revisiones) },
      { name: quien ? `Atendidas por ${quien}` : "Atendidas por el equipo", labels: flujo.map((d) => d.etiqueta), values: flujo.map((d) => d.atendidas) },
      ...(quien ? [] : [{ name: "Resueltas por los datos", labels: flujo.map((d) => d.etiqueta), values: flujo.map((d) => d.porDatos) }]),
    ], {
      x: p(34), y: p(96), w: p(892), h: p(380), barDir: "col", barGrouping: "clustered", chartColors: [ROJO, AZUL_OSCURO, VERDE], barGapWidthPct: 40,
      showValue: flujo.length <= 31, dataLabelFontSize: 7, dataLabelColor: TINTA, ...ejes,
    });
  } else vacio(entrada, "No se levantaron alertas en el periodo.");

  // 4. Work by person: a stacked bar per person and the table.
  const porTipo = trabajoPorTipo(gente);
  const trabajo = contenido("Trabajo por persona", `${periodo} · un día procesado cuenta una vez, aunque lea todas las oficinas`);
  if (porTipo.length) {
    trabajo.addChart(pptx.ChartType.bar, TIPOS_DE_TRABAJO.map((t) => ({
      name: t.etiqueta, labels: porTipo.map((x) => x.nombre), values: porTipo.map((x) => x[t.clave]),
    })), {
      x: p(34), y: p(96), w: p(430), h: p(390), barDir: "bar", barGrouping: "stacked", chartColors: TRABAJO, showValue: false, ...ejes,
    });
    tabla(trabajo, [
      { titulo: "Persona", ancho: 140, align: "left" }, { titulo: "Días", ancho: 46 }, { titulo: "Alertas", ancho: 52 },
      { titulo: "Correcc.", ancho: 56 }, { titulo: "Docs.", ancho: 46 }, { titulo: "Total", ancho: 50 },
    ], [...gente, ...(datos.automatic && !persona ? [datos.automatic] : [])].slice(0, 16).map((x) => [
      x === datos.automatic ? "Automático (correo)" : nombreDePersona(x.persona), x.dias, x.alertas, x.correcciones, x.documentos, x.acciones,
    ]), { x: 476, y: 100, w: 390 });
  } else vacio(trabajo, "Nadie trabajó en este cliente en el periodo.");

  // 5. Response times: per person, and per day.
  const tiempos = contenido("Tiempo para atender una alerta", `${periodo} · desde que se levanta hasta que alguien la atiende`);
  if (respuestas.length) {
    tabla(tiempos, [
      { titulo: "Persona", ancho: 150, align: "left" }, { titulo: "Atendidas", ancho: 64 }, { titulo: "Mediana", ancho: 70 }, { titulo: "Promedio", ancho: 70 },
      ...datos.responses.rangos.map((r) => ({ titulo: r.etiqueta, ancho: 72 })),
    ], respuestas.slice(0, 8).map((r) => [
      nombreDePersona(r.persona), r.atendidas, horasLegibles(r.medianaHoras), horasLegibles(r.promedioHoras), ...r.rangos.map((x) => x.n),
    ]), { x: 34, y: 96, w: 642 });
    const porDia = serieDeRespuesta(datos.responseDays, persona);
    if (porDia.length) {
      tiempos.addChart(pptx.ChartType.bar, [{ name: "Espera promedio (h)", labels: porDia.map((d) => d.etiqueta), values: porDia.map((d) => d.promedioHoras || 0) }], {
        x: p(34), y: p(290), w: p(892), h: p(196), barDir: "col", chartColors: [NARANJA], showValue: porDia.length <= 31, dataLabelFontSize: 7, dataLabelFormatCode: "0.0", ...ejes,
      });
    }
  } else vacio(tiempos, "Nadie atendió alertas en el periodo.");

  // 6. The queue today: by age and by region.
  const cola = contenido("Cola de trabajo hoy", "Lo que está abierto hoy, por cuánto lleva esperando y por región");
  const etiquetas = ["Descuadres", "Por revisar", "Falta información"];
  const tipos = ["descuadres", "revisiones", "faltantes"];
  cola.addChart(pptx.ChartType.bar, datos.queue.edades.map((e) => ({
    name: e.etiqueta, labels: etiquetas, values: tipos.map((t) => datos.queue.abiertas[t]?.[e.clave] || 0),
  })), {
    x: p(34), y: p(96), w: p(440), h: p(390), barDir: "bar", barGrouping: "stacked", chartColors: ["93A3C4", NARANJA, "E07D20", ROJO], showValue: false, ...ejes,
  });
  const regiones = colaPorRegion(datos.backlog);
  if (regiones.length) {
    tabla(cola, [
      { titulo: "Región", ancho: 130, align: "left" }, { titulo: "Oficinas", ancho: 60 }, { titulo: "Descuadres", ancho: 70 },
      { titulo: "Por revisar", ancho: 70 }, { titulo: "Falta info.", ancho: 70 },
    ], regiones.slice(0, 16).map((r) => [r.region, r.oficinas, r.descuadres, r.revisiones, r.faltantes]), { x: 490, y: 100, w: 400 });
  }

  // 7. The offices with the most open work.
  tablaPaginada("Oficinas con más trabajo abierto", "Lo que cada oficina tiene abierto hoy, los descuadres primero", [
    { titulo: "Código", ancho: 70 }, { titulo: "Oficina", ancho: 250, align: "left" }, { titulo: "Región", ancho: 150, align: "left" },
    { titulo: "Descuadres", ancho: 90 }, { titulo: "Por revisar", ancho: 90 }, { titulo: "Falta información", ancho: 110 }, { titulo: "Más antigua", ancho: 132 },
  ], (datos.backlog || []).slice(0, FILAS_POR_TABLA * 2).map((o) => [
    o.officeCode, o.officeName, o.region || "—", o.descuadres, o.revisiones, o.faltantes, o.diasMasAntigua ? `${o.diasMasAntigua} días` : "hoy",
  ]), "Ninguna oficina tiene trabajo abierto.");

  // 8. Each kind of alert.
  tablaPaginada("Alertas por tipo", `${periodo} · cuántas atendió el equipo, cuántas resolvieron los datos y cuántas siguen abiertas`, [
    { titulo: "Alerta", ancho: 300, align: "left" }, { titulo: "Tipo", ancho: 120 }, { titulo: "Levantadas", ancho: 90 },
    { titulo: "Atendidas", ancho: 90 }, { titulo: "Por los datos", ancho: 100 }, { titulo: "Abiertas", ancho: 80 }, { titulo: "Mediana", ancho: 112 },
  ], (datos.rules || []).slice(0, FILAS_POR_TABLA).map((r) => [
    nombreDeAlerta(r.ruleKey), TIPO_DE_ALERTA[r.severity] || r.severity, r.levantadas, r.atendidas, r.porDatos, r.abiertas, horasLegibles(r.medianaHoras),
  ]), "No se levantaron alertas en el periodo.");

  // 9. Each day's flow.
  const dias = datos.days.filter((d) => !persona || d.procesadoPor === persona);
  tablaPaginada("Flujo de cada día", periodo, [
    { titulo: "Día", ancho: 80 }, { titulo: "Procesado por", ancho: 170, align: "left" }, { titulo: "Hora", ancho: 90 },
    { titulo: "Leídas", ancho: 70 }, { titulo: "Con error", ancho: 75 }, { titulo: "Sin filas", ancho: 75 },
    { titulo: "Cuadran", ancho: 80 }, { titulo: "Correo del día", ancho: 110 }, { titulo: "Correos oficinas", ancho: 142 },
  ], dias.map((d) => [
    `${d.dia.slice(8, 10)}-${d.dia.slice(5, 7)}-${d.dia.slice(0, 4)}`,
    d.procesadoPor ? (d.viaCorreo ? "Por correo" : nombreDePersona(d.procesadoPor)) : "Sin procesar",
    horaBogota(d.fin), d.leidas, d.conError, d.sinFilas,
    d.cierres ? `${Math.round((d.cuadrados / d.cierres) * 100)} %` : "—",
    d.correoDelDia ? horaBogota(d.correoDelDia) : "—", d.correosDeOficinas,
  ]), quien ? "Esta persona no procesó días en el periodo." : "No hay cierres en el periodo.");

  // 10. Thanks.
  const fin = pptx.addSlide();
  fin.background = { color: "FCFCFC" };
  fin.addImage({ path: I.degradado, x: 0, y: p(11), w: p(456), h: p(266), sizing: { type: "cover", w: p(456), h: p(266) } });
  fin.addImage({ path: I.gracias, x: p(456), y: p(11), w: p(504), h: p(266), sizing: { type: "cover", w: p(504), h: p(266) } });
  fin.addImage({ path: I.bandaSuperior, x: 0, y: 0, w: p(960), h: p(11) });
  fin.addImage({ path: I.logoBrinksAzul, x: p(792), y: p(38), w: p(113), h: p(19.5) });
  fin.addText("GRACIAS", { x: p(34), y: p(30), w: p(300), h: p(46), fontFace: FUENTE, fontSize: 28, color: "FFFFFF" });
  regla(fin);
  pie(fin);
  return pptx;
}

/** Builds and saves the presentation. pptxgenjs and the pictures are loaded here, on the click. */
export async function descargarPresentacionEquipo(datos, opciones) {
  const [{ default: PptxGenJS }, imagenes] = await Promise.all([
    import("pptxgenjs"),
    Promise.all(USADAS.map(async (clave) => [clave, await comoDatos(IMAGENES[clave])])).then(Object.fromEntries),
  ]);
  const pptx = presentacionDelEquipo(PptxGenJS, datos, { ...opciones, imagenes });
  await pptx.writeFile({ fileName: opciones.nombreArchivo || `Informe del equipo ${opciones.cliente}.pptx` });
}
